// ============================================================
// ORDINE OK — /ordine-ok
// ============================================================
// Raggiunta dopo il redirect di Stripe Embedded Checkout
// (return_url=/ordine-ok?session_id={CHECKOUT_SESSION_ID}).
// L'esito del pagamento viene sempre verificato lato backend —
// non ci si fida mai di quello che arriva dal client.

import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useCart } from '../../../contexts/CartContext';
import { orderService } from '../../../services';
import type { CheckoutSessionResult } from '../../../services/interfaces/IOrderService';
import './OrdineOk.css';

type ViewState = 'loading' | 'paid' | 'unpaid' | 'error';

const OrdineOk: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const sessionId = searchParams.get('session_id');

  const [view, setView] = useState<ViewState>('loading');
  const [result, setResult] = useState<CheckoutSessionResult | null>(null);
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      navigate('/', { replace: true });
      return;
    }

    orderService
      .getCheckoutSession(sessionId)
      .then((res) => {
        setResult(res);
        if (res.status === 'paid' || res.status === 'no_payment_required') {
          setView('paid');
          if (!clearedRef.current) {
            clearedRef.current = true;
            clearCart();
          }
        } else {
          setView('unpaid');
        }
      })
      .catch(() => setView('error'));
  }, [sessionId, navigate, clearCart]);

  if (view === 'loading') {
    return (
      <div className="ordine-ok-page">
        <div className="container py-5">
          <div className="ordine-ok-card">
            <div
              className="spinner-border text-secondary mb-3"
              role="status"
              aria-hidden="true"
            />
            <p className="ordine-ok-subtitle mb-0">
              Verifica del pagamento in corso...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'error' || view === 'unpaid') {
    return (
      <div className="ordine-ok-page">
        <div className="container py-5">
          <div className="ordine-ok-card">
            <div className="ordine-ok-icon">
              <i className="bi bi-x-circle-fill" style={{ color: '#b91c1c' }} />
            </div>
            <h1 className="ordine-ok-title">
              {view === 'unpaid'
                ? 'Pagamento non completato'
                : 'Non riusciamo a verificare il pagamento'}
            </h1>
            <p className="ordine-ok-subtitle">
              {view === 'unpaid'
                ? 'Il pagamento non risulta andato a buon fine. Nessun addebito è stato effettuato.'
                : "Riprova tra qualche minuto o contatta l'assistenza se il problema persiste."}
            </p>
            <div className="ordine-ok-actions">
              <Link to="/rimettiti-al-centro" className="ordine-ok-btn-primary">
                <i className="bi bi-arrow-repeat me-2" />
                Riprova
              </Link>
              <Link to="/" className="ordine-ok-btn-secondary">
                Torna alla home
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ordine-ok-page">
      <div className="container py-5">
        <div className="ordine-ok-card">
          {/* Icona successo animata */}
          <div className="ordine-ok-icon">
            <i className="bi bi-check-circle-fill" />
          </div>

          <h1 className="ordine-ok-title">Acquisto completato!</h1>
          <p className="ordine-ok-subtitle">
            Pagamento di{' '}
            <strong>€ {(result?.amountTotal ?? 0).toFixed(2)}</strong> ricevuto
            con successo.
          </p>

          {/* Corsi acquistati */}
          {result && result.courseTitles.length > 0 && (
            <div className="ordine-ok-courses">
              <p className="ordine-ok-courses-label">Corsi acquistati:</p>
              <ul>
                {result.courseTitles.map((title, i) => (
                  <li key={i}>
                    <i className="bi bi-check2 me-2 text-success" />
                    {title}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Info accesso */}
          <div className="ordine-ok-info">
            <i className="bi bi-bell me-2" />
            <span>
              Il corso è già disponibile: lo trovi subito tra i tuoi corsi e
              trovi la conferma anche tra le notifiche.
            </span>
          </div>

          {/* CTA */}
          <div className="ordine-ok-actions">
            <Link to="/dashboard/corsi" className="ordine-ok-btn-primary">
              <i className="bi bi-play-circle me-2" />
              Vai ai miei corsi
            </Link>
            <Link to="/" className="ordine-ok-btn-secondary">
              Torna alla home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrdineOk;
